import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../../components/ui/Button';
import { TONE } from '../../components/ui/tokens';
import type { PackCountGuardIssue } from '../../utils/invoiceLineMath';

/**
 * D65 (owner, 2026-09-25) — the Units / Case guard's one-click answer.
 *
 * `packCountGuardIssue` (mirrored, `invoiceLineMath`) decides live, from the
 * row on screen, whether Units / Case still needs proving: a pack of ONE with
 * no size stated ("sold_as_one" — is this box really one item, or does it
 * hold ten bars?), or a count that exactly matches a size printed on the same
 * line ("matches_size" — a "5 KG" bag with 5 typed into the count). Both are
 * refused at Confirm & sync until answered.
 *
 * One button clears either, because both answers are the SAME statement:
 * "I looked at the paper and the Units / Case on this line, N, is right."
 * That is `packCountConfirmed = N` — a number, not a tick, so it goes stale
 * by itself the moment Units / Case is edited again and nobody has to
 * remember to clear it.
 */
export interface PackCountConfirmPromptProps {
  issue: PackCountGuardIssue;
  /** The Units / Case value being vouched for — `packCountConfirmed`'s new value. */
  packCount: number;
  lineNo: number;
  /** View mode: the question is shown, and cannot be answered from here. */
  readOnly?: boolean;
  onAnswer: (packCount: number) => void;
}

export function PackCountConfirmPrompt({
  issue,
  packCount,
  lineNo,
  readOnly = false,
  onAnswer,
}: PackCountConfirmPromptProps) {
  const buttonLabel = issue.kind === 'sold_as_one' ? 'Sold as one item' : `Yes, ${packCount} per pack is right`;

  return (
    <div
      role="group"
      aria-label={`Line ${lineNo}: confirm Units / Case`}
      className={`sticky left-0 flex w-fit max-w-3xl flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${TONE.attention}`}
    >
      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span>{`Line ${lineNo}: ${issue.reason}`}</span>
      <Button size="sm" disabled={readOnly} onClick={() => onAnswer(packCount)}>
        {buttonLabel}
      </Button>
    </div>
  );
}

export default PackCountConfirmPrompt;
